import { Injectable } from '@angular/core';

export interface ConvertSvgToPngOptions {
  readonly backgroundColor?: string;
  readonly scale?: number;
}

@Injectable({ providedIn: 'root' })
export class ConvertService {
  async convertSvgToPng(svgElement: SVGSVGElement, options: ConvertSvgToPngOptions = {}): Promise<Blob> {
    const { width, height } = this.getSvgSize(svgElement);
    const svg = svgElement.cloneNode(true) as SVGSVGElement;
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('width', `${width}`);
    svg.setAttribute('height', `${height}`);

    if (!svg.getAttribute('viewBox')) {
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }

    const svgText = new XMLSerializer().serializeToString(svg);
    const svgUrl = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' }));

    try {
      const image = await this.loadImage(svgUrl);
      const scale = options.scale ?? globalThis.devicePixelRatio ?? 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(width * scale);
      canvas.height = Math.ceil(height * scale);

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Canvas 2D context is not available.');
      }

      context.setTransform(scale, 0, 0, scale, 0, 0);
      if (options.backgroundColor) {
        context.fillStyle = options.backgroundColor;
        context.fillRect(0, 0, width, height);
      }

      context.drawImage(image, 0, 0, width, height);
      return await this.canvasToPng(canvas);
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  private getSvgSize(svgElement: SVGSVGElement): { width: number; height: number } {
    const rect = svgElement.getBoundingClientRect();
    const width = this.normalizeSize(rect.width) ?? this.normalizeSize(svgElement.width.baseVal.value) ?? this.getViewBoxSize(svgElement).width;
    const height = this.normalizeSize(rect.height) ?? this.normalizeSize(svgElement.height.baseVal.value) ?? this.getViewBoxSize(svgElement).height;

    return {
      width: width ?? 1,
      height: height ?? 1,
    };
  }

  private getViewBoxSize(svgElement: SVGSVGElement): { width?: number; height?: number } {
    const viewBox = svgElement.viewBox.baseVal;
    return {
      width: this.normalizeSize(viewBox.width),
      height: this.normalizeSize(viewBox.height),
    };
  }

  private normalizeSize(value: number): number | undefined {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load SVG image.'));
      image.src = url;
    });
  }

  private canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('Unable to convert SVG to PNG.'));
      }, 'image/png');
    });
  }
}